import React, { useState } from 'react';
import WellnessAssessmentModal from './WellnessAssessmentModal';

// ── Types ─────────────────────────────────────────────────────────────────
export type WellnessTypeStatus = {
    type: string;
    status: string | null;
    attemptId: string | null;
    expiresAt: string | null;
    startTime: string | null;
    endTime: string | null;
};

export type WellnessInviteData = {
    inviteId: string;
    assessmentId: string | null;
    jobOpeningId: string;
    jobTitle: string | null;
    organizationName: string | null;
    sentAt: string | null;
    types: WellnessTypeStatus[];
};

// ── Status styling (mirrors statusConfig in ParticipantDashboard) ──────────
const typeStatusConfig: Record<string, { label: string; className: string }> = {
    'Invite Sent': {
        label: 'Not Started',
        className: 'bg-amber-50 text-amber-600 border border-amber-200',
    },
    'In Progress': {
        label: 'In Progress',
        className: 'bg-blue-50 text-blue-600 border border-blue-200',
    },
    'Completed': {
        label: 'Completed',
        className: 'bg-emerald-50 text-emerald-600 border border-emerald-200',
    },
    'Expired': {
        label: 'Expired',
        className: 'bg-red-50 text-red-500 border border-red-200',
    },
    'Superseded': {
        label: 'Superseded',
        className: 'bg-slate-100 text-slate-400 border border-slate-200',
    },
};

const getTypeStatusConfig = (status: string | null): { label: string; className: string } =>
    (status ? typeStatusConfig[status] : undefined) ?? {
        label: status ?? 'Unknown',
        className: 'bg-slate-100 text-slate-500 border border-slate-200',
    };

const formatDate = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

const daysRemaining = (iso: string | null) => {
    if (!iso) return null;
    const diffMs = new Date(iso).getTime() - Date.now();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const isActionableInvite = (invite: WellnessInviteData) =>
    invite.types.some((t) => t.status === 'Invite Sent' || t.status === 'In Progress');

// ── Type Pill ────────────────────────────────────────────────────────────
const WellnessTypePill = ({ item }: { item: WellnessTypeStatus }) => {
    const { label, className } = getTypeStatusConfig(item.status);
    const daysLeft = daysRemaining(item.expiresAt);
    const isActionable = item.status === 'Invite Sent' || item.status === 'In Progress';

    return (
        <div className='flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2'>
            <span className='text-[13px] font-semibold text-slate-700'>{ item.type }</span>
            <div className='flex items-center gap-2'>
                { isActionable && daysLeft !== null && (
                    <span className={`text-[11px] font-medium ${daysLeft <= 1 ? 'text-red-500' : 'text-slate-400'}`}>
            { daysLeft > 0 ? `${daysLeft}d left` : 'Due today' }
          </span>
                ) }
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${className}`}>
          { label }
        </span>
            </div>
        </div>
    );
};

// ── Invite Card ──────────────────────────────────────────────────────────
const WellnessInviteCard = ({ invite, onClick }: { invite: WellnessInviteData; onClick: () => void }) => {
    const jobTitle = invite.jobTitle ?? 'Wellness Screening';
    const sentDate = formatDate(invite.sentAt);
    const actionable = isActionableInvite(invite);

    return (
        <div
            onClick={onClick}
            className='bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-200 p-6 flex flex-col gap-4 cursor-pointer'
        >
            <div className='flex items-start justify-between'>
                <div className='w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0'>
                    <svg className='w-5 h-5 text-purple-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5}
                              d='M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78l1.06 1.06L12 21l7.78-7.55 1.06-1.06a5.5 5.5 0 000-7.78z'/>
                    </svg>
                </div>
                <span className='text-[11px] font-medium text-slate-400'>
          { invite.types.length } assessment{ invite.types.length !== 1 ? 's' : '' }
        </span>
            </div>

            <div>
                <p className='text-slate-800 font-semibold text-[15px] leading-snug'>{ jobTitle }</p>
                { invite.organizationName && (
                    <p className='text-slate-400 text-xs mt-1'>{ invite.organizationName }</p>
                ) }
                { sentDate && (
                    <p className='text-slate-400 text-xs mt-1'>Sent { sentDate }</p>
                ) }
            </div>

            <div className='pt-3 border-t border-slate-100 flex flex-col gap-2'>
                { invite.types.map((item) => (
                    <WellnessTypePill key={item.type} item={item} />
                )) }
            </div>

            { actionable && (
                <div className='text-[11px] font-semibold text-purple-500 flex items-center gap-1'>
                    Continue
                    <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                    </svg>
                </div>
            ) }
        </div>
    );
};

// ── Pagination (mirrors ParticipantDashboard's Pagination) ─────────────────
const WellnessPagination = ({
                                page,
                                totalCount,
                                pageSize,
                                onPageChange,
                            }: {
    page: number;
    totalCount: number;
    pageSize: number;
    onPageChange: (p: number) => void;
}) => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (totalPages <= 1) return null;

    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, totalCount);

    return (
        <div className='relative z-10 flex items-center justify-between py-4'>
            <p className='text-xs text-slate-400'>
                Showing <span className='font-semibold text-slate-600'>{ from }–{ to }</span> of{ ' ' }
                <span className='font-semibold text-slate-600'>{ totalCount }</span>
            </p>
            <div className='flex items-center gap-1'>
                <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 1}
                    className='w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
                >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
                    </svg>
                </button>

                { Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                        if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) {
                            acc.push('...');
                        }
                        acc.push(p);
                        return acc;
                    }, [])
                    .map((p, idx) =>
                            p === '...' ? (
                                <span key={`ellipsis-${idx}`} className='w-8 h-8 flex items-center justify-center text-slate-400 text-sm'>
                …
              </span>
                            ) : (
                                <button
                                    key={p}
                                    onClick={() => onPageChange(p as number)}
                                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                                        page === p
                                            ? 'bg-emerald-500 text-white shadow-sm'
                                            : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    { p }
                                </button>
                            ),
                    ) }

                <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page === totalPages}
                    className='w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
                >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                    </svg>
                </button>
            </div>
        </div>
    );
};

// ── Empty State ──────────────────────────────────────────────────────────
const WellnessEmptyState = () => (
    <div className='relative z-10 flex flex-col items-center justify-center py-24 text-center'>
        <div className='w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-4'>
            <svg className='w-8 h-8 text-purple-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5}
                      d='M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78l1.06 1.06L12 21l7.78-7.55 1.06-1.06a5.5 5.5 0 000-7.78z'/>
            </svg>
        </div>
        <p className='text-slate-700 font-semibold text-lg'>No Wellness Assessments</p>
        <p className='text-slate-400 text-sm mt-1'>Wellness screenings you are invited to will appear here.</p>
    </div>
);

// ── Main Component (presentational - data/paging owned by the parent) ─────
const WellnessDashboard = ({
                               invites,
                               totalCount,
                               loading,
                               page,
                               pageSize,
                               onPageChange,
                               onRefresh,
                           }: {
    invites: WellnessInviteData[];
    totalCount: number;
    loading: boolean;
    page: number;
    pageSize: number;
    onPageChange: (p: number) => void;
    onRefresh?: () => void; // called as the candidate progresses, so the parent can re-fetch and update card statuses
}) => {
    const [selectedInviteId, setSelectedInviteId] = useState<string | null>(null);

    if (loading) {
        return (
            <div className='relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                { [...Array(3)].map((_, index) => (
                    <div key={index} className='h-[220px] animate-pulse bg-gray-200 rounded-2xl'/>
                )) }
            </div>
        );
    }

    if (invites.length === 0) {
        return <WellnessEmptyState />;
    }

    return (
        <>
            <div className='relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                { invites.map((invite) => (
                    <WellnessInviteCard
                        key={invite.inviteId}
                        invite={invite}
                        onClick={() => setSelectedInviteId(invite.inviteId)}
                    />
                )) }
            </div>
            <WellnessPagination
                page={page}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={onPageChange}
            />

            { selectedInviteId && (
                <WellnessAssessmentModal
                    inviteId={selectedInviteId}
                    isOpen={selectedInviteId !== null}
                    onClose={() => {
                        setSelectedInviteId(null);
                        onRefresh?.();
                    }}
                    onProgress={onRefresh}
                />
            ) }
        </>
    );
};

export default WellnessDashboard;